let csrfToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Register a callback invoked when any request gets a 401 (session expired/invalid). */
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

async function ensureCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;
  const res = await fetch('/api/csrf', { credentials: 'same-origin' });
  if (!res.ok) throw new ApiError(res.status, 'csrf', `Could not get a CSRF token (HTTP ${res.status})`);
  const data = (await res.json().catch(() => null)) as { token?: string } | null;
  if (!data?.token) throw new ApiError(res.status, 'csrf', 'Malformed CSRF response');
  csrfToken = data.token;
  return csrfToken;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const opts: RequestInit = { method, credentials: 'same-origin', headers };
  if (body !== undefined) {
    headers['content-type'] = 'application/json';
    opts.body = JSON.stringify(body);
  }
  const mutating = method !== 'GET' && method !== 'HEAD';
  if (mutating) headers['x-csrf-token'] = await ensureCsrf();

  let res = await fetch(path, opts);
  if (res.status === 403 && mutating) {
    // Stale CSRF token — refetch once and retry.
    csrfToken = null;
    headers['x-csrf-token'] = await ensureCsrf();
    res = await fetch(path, opts);
  }
  if (res.status === 401 && !path.startsWith('/api/auth/')) {
    // Session expired/invalid: drop the CSRF token and let the app fall back to the login gate
    // instead of leaving pages stuck on an infinite spinner.
    csrfToken = null;
    onUnauthorized?.();
  }

  if (res.status === 204) return null as T;
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null; // non-JSON body (e.g. an HTML error page) — fall back to statusText below
  }
  if (!res.ok) {
    // Tolerate our shape ({ error: { code, message } }) and Fastify's default
    // ({ code, error, message }) so error messages always surface.
    const d = data as { error?: unknown; code?: string; message?: string } | null;
    const e = d?.error;
    const obj = e && typeof e === 'object' ? (e as { code?: string; message?: string }) : null;
    const code = obj?.code ?? d?.code ?? 'error';
    const message = obj?.message ?? (typeof e === 'string' ? e : undefined) ?? d?.message ?? res.statusText;
    throw new ApiError(res.status, code, message);
  }
  return data as T;
}

export const api = {
  get: <T>(p: string) => request<T>('GET', p),
  post: <T>(p: string, b?: unknown) => request<T>('POST', p, b),
  patch: <T>(p: string, b?: unknown) => request<T>('PATCH', p, b),
  del: <T>(p: string, b?: unknown) => request<T>('DELETE', p, b),
  resetCsrf: () => {
    csrfToken = null;
  },
};
